<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

// The triggers default to on, so enabling native mode alone gives a working flow.
const DEFAULT_N8N_CONFIG = Object.freeze({
  n8n_native: false,
  n8n_start_on_message: true,
  n8n_start_on_manual_pending: true,
  n8n_start_on_reopen: true,
});

export default {
  components: {
    LoadingState,
    SettingsFieldSection,
    NextButton,
    SelectInput,
    ToggleSwitch,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      n8nConfig: { ...DEFAULT_N8N_CONFIG },
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    currentInboxId() {
      return this.inbox?.id || this.$route.params.inboxId;
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](
        this.currentInboxId
      );
    },
    activeInboxBotConfig() {
      return this.$store.getters['agentBots/getAgentBotInboxConfig'](
        this.currentInboxId
      );
    },
    botOptions() {
      return this.agentBots.map(bot => ({ value: bot.id, label: bot.name }));
    },
    n8nTriggers() {
      return [
        {
          key: 'n8n_start_on_message',
          label: this.$t('AGENT_BOTS.BOT_CONFIGURATION.N8N.START_ON_MESSAGE'),
          help: this.$t(
            'AGENT_BOTS.BOT_CONFIGURATION.N8N.START_ON_MESSAGE_HELP'
          ),
        },
        {
          key: 'n8n_start_on_manual_pending',
          label: this.$t(
            'AGENT_BOTS.BOT_CONFIGURATION.N8N.START_ON_MANUAL_PENDING'
          ),
          help: this.$t(
            'AGENT_BOTS.BOT_CONFIGURATION.N8N.START_ON_MANUAL_PENDING_HELP'
          ),
        },
        {
          key: 'n8n_start_on_reopen',
          label: this.$t('AGENT_BOTS.BOT_CONFIGURATION.N8N.START_ON_REOPEN'),
          help: this.$t(
            'AGENT_BOTS.BOT_CONFIGURATION.N8N.START_ON_REOPEN_HELP'
          ),
        },
      ];
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot?.id || null;
    },
    activeInboxBotConfig: {
      handler(config) {
        this.n8nConfig = { ...DEFAULT_N8N_CONFIG, ...config };
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.fetchBotData();
  },

  methods: {
    fetchBotData() {
      this.$store.dispatch('agentBots/get');
      this.$store.dispatch('agentBots/fetchAgentBotInbox', this.currentInboxId);
    },
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.currentInboxId,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
          botConfig: this.n8nConfig,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.currentInboxId,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form v-else @submit.prevent="updateActiveAgentBot">
      <SettingsFieldSection
        :label="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :help-text="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
        class="[&>div]:!items-start"
      >
        <SelectInput
          v-model="selectedAgentBotId"
          :placeholder="$t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER')"
          :options="botOptions"
        />
        <div
          v-if="selectedAgentBotId"
          class="mt-4 overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-col gap-1 px-4 py-4 border-b border-n-weak">
            <span class="text-heading-3 text-n-slate-12">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.N8N.TITLE') }}
            </span>
            <span class="text-body-small text-n-slate-11">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.N8N.DESC') }}
            </span>
          </div>

          <div class="flex items-start justify-between gap-4 px-4 py-4">
            <div class="flex flex-col gap-1">
              <span class="text-body-main text-n-slate-12">
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.N8N.MODE_LABEL') }}
              </span>
              <span class="text-body-small text-n-slate-11">
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.N8N.MODE_HELP') }}
              </span>
            </div>
            <ToggleSwitch v-model="n8nConfig.n8n_native" />
          </div>

          <div
            v-if="n8nConfig.n8n_native"
            class="px-4 py-4 border-t border-n-weak"
          >
            <span class="text-body-small text-n-slate-11">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.N8N.TRIGGERS_LABEL') }}
            </span>
            <div
              v-for="trigger in n8nTriggers"
              :key="trigger.key"
              class="flex items-start justify-between gap-4 mt-4"
            >
              <div class="flex flex-col gap-1">
                <span class="text-body-main text-n-slate-12">
                  {{ trigger.label }}
                </span>
                <span class="text-body-small text-n-slate-11">
                  {{ trigger.help }}
                </span>
              </div>
              <ToggleSwitch v-model="n8nConfig[trigger.key]" />
            </div>
          </div>
        </div>
        <template #extra>
          <div class="grid grid-cols-1 mt-3 lg:grid-cols-8">
            <div class="invisible col-span-1 lg:col-span-2" />
            <div class="flex col-span-1 gap-2 mx-1 lg:col-span-6">
              <NextButton
                type="submit"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                :is-loading="uiFlags.isSettingAgentBot"
              />
              <NextButton
                type="button"
                :disabled="!selectedAgentBotId"
                :is-loading="uiFlags.isDisconnecting"
                faded
                ruby
                @click="disconnectBot"
              >
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
              </NextButton>
            </div>
          </div>
        </template>
      </SettingsFieldSection>
    </form>
  </div>
</template>
